/**
 * The CURRENT session bearer, and whether a 401 is about it.
 *
 * WHY THIS EXISTS -- prod, 2026-10-01 (AdventHealth QR entry).
 *
 * 7 of ~16 iPhone visitors minted an anonymous storefront session, browsed,
 * and their first add-to-cart went out with NO bearer. ALB evidence: the
 * token was in localStorage at mint and gone ~13s later with zero requests in
 * between -- WebKit dropped the key, our code did not remove it. localStorage
 * was the session's ONLY copy of the bearer, so the page kept the account id
 * (Vuex) and silently lost the credential.
 *
 * READ ORDER: localStorage first, then -- for an ANONYMOUS storefront session
 * only -- the in-memory copy in the Pinia account store, re-mirrored into
 * storage when used.
 *
 * Storage stays first so cross-tab behaviour is unchanged: a sign-in in
 * another tab still takes effect here, as it always has.
 *
 * The memory fallback is scoped to the anonymous session because a
 * CREDENTIALED token missing from storage usually means it was removed on
 * purpose (a logout in another tab), and logout does not revoke the token
 * server-side -- serving it from memory and re-mirroring it would silently
 * log that user back in. An anonymous session carries only its own cart, and
 * it has no sign-in to fall back on, which is exactly why it needs the
 * lifeline.
 */
// THIS MODULE MUST STAY A LEAF. `@/axios` imports it, so anything it imports
// is evaluated while `@/axios` is still mid-evaluation. Importing the account
// store here (it imports `@/axios`, and through the auth interceptor reaches
// `@/services/api`) made `services/api` capture an undefined instance: every
// service call threw and boot requests went out with no bearer, logging a
// signed-in shopper out on reload (sandbox 8088, 2026-10-01). So the store is
// read through Pinia's state registry by id, not imported. Regression net:
// __tests__/axios-import-cycle.spec.js.
import { resolveBearer } from '@/router/sessionProvenance'

function clean(token) {
    if (token == null) return null
    const value = String(token).trim().replace(/^["']+|["']+$/g, '')
    return value.length > 0 ? value : null
}

/**
 * The bearer the next request should carry, or null. When it came from the
 * in-memory copy (the browser dropped the storage key), it is written back.
 * Which bearer, and whether memory may stand in, is decided by
 * `resolveBearer` in router/sessionProvenance.js -- the same module that
 * holds the one anonymous-session predicate, so the two cannot disagree.
 */
export function currentBearer() {
    const { bearer, fromMemory } = resolveBearer()
    if (fromMemory) {
        try {
            localStorage.setItem('token', bearer)
        } catch {
            // Storage refused; memory still serves this page.
        }
    }
    return bearer
}

/** The bearer a request was SENT with (from its Authorization header), or null. */
export function sentBearer(config) {
    const headers = config && config.headers
    if (!headers) return null
    const raw =
        typeof headers.get === 'function'
            ? headers.get('Authorization')
            : headers.Authorization ?? headers.authorization
    if (typeof raw !== 'string') return null
    return clean(raw.replace(/^Bearer\s+/i, ''))
}

/**
 * True when a failed request was sent with a bearer that is no longer the
 * current one -- so its 401 describes a session that has already been
 * replaced, and acting on it would end the REPLACEMENT.
 */
export function isSupersededSessionError(error) {
    const config = error && error.config
    if (!config) return false
    return sentBearer(config) !== currentBearer()
}
